import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/router-devtools'
import { AnimatePresence, motion } from 'motion/react'
import { useState, useEffect, useRef } from 'react'
import { Header } from '@/components/Header'
import { Sidebar } from '@/components/Sidebar'
import { IconRail } from '@/components/IconRail'
import { ImpersonationControls } from '@/components/admin/ImpersonationControls'
import { AlphaBanner } from '@/components/AlphaBanner'
import { ToastProvider } from '@/contexts/ToastContext'
import { apiConfig } from '@/config/api'

export const Route = createRootRoute({
  component: RootComponent,
})

function RootComponent() {
  const [isNavOpen, setIsNavOpen] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const iconRailRef = useRef<HTMLDivElement>(null)
  const routerState = useRouterState()

  // Close sidebar on route navigation
  useEffect(() => {
    setIsNavOpen(false)
  }, [routerState.location.pathname])

  // Close sidebar on Escape key or click outside
  useEffect(() => {
    if (!isNavOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsNavOpen(false)
    }

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node
      const outsideSidebar = sidebarRef.current && !sidebarRef.current.contains(target)
      const outsideIconRail = iconRailRef.current && !iconRailRef.current.contains(target)
      if (outsideSidebar && outsideIconRail) setIsNavOpen(false)
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleMouseDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [isNavOpen])

  return (
    <ToastProvider>
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        <div ref={iconRailRef}>
          <IconRail
            onToggleNav={() => setIsNavOpen((prev) => !prev)}
            isNavOpen={isNavOpen}
          />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', marginLeft: '50px' }}>
          <Header />
          <main style={{ flex: 1 }}>
            <Outlet />
          </main>
        </div>
        <AnimatePresence>
          {isNavOpen && (
            <motion.div
              style={{
                position: 'fixed',
                top: 0,
                left: '50px',
                height: '100vh',
                zIndex: 1029,
                pointerEvents: 'none',
              }}
              initial={{ y: '-100%' }}
              animate={{ y: 0 }}
              exit={{ y: '-100%' }}
              transition={{ type: 'tween', duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            >
              <div ref={sidebarRef} style={{ pointerEvents: 'auto' }}>
                <Sidebar />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ImpersonationControls />
      <AlphaBanner />
      {apiConfig.getEnvironment() !== 'production' && <TanStackRouterDevtools />}
    </ToastProvider>
  )
}
